import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { SA, aapi, apiErr, download, fmtDT, useA, useAdminApi, useAuth } from "./lib";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

export function ChatsPage() {
  const a = useA();
  const { lang } = useI18n();
  const { data, reload } = useAdminApi("/admin/chats");
  const { user } = useAuth();
  const isSA = user?.role === SA;
  const [sel, setSel] = useState(null);
  const [picked, setPicked] = useState([]);
  const toggle = (sid) => setPicked((p) => (p.includes(sid) ? p.filter((x) => x !== sid) : [...p, sid]));
  const remove = async (sids) => {
    if (!sids.length || !window.confirm(a(`Delete ${sids.length} chat session(s) permanently?`, `حذف ${sids.length} جلسة نهائياً؟`))) return;
    try {
      const r = sids.length === 1 ? await aapi.delete(`/admin/chats/${sids[0]}`) : await aapi.post("/admin/chats/bulk-delete", { session_ids: sids });
      toast.success(a(`Deleted ${r.data.deleted}`, `تم حذف ${r.data.deleted}`));
      setPicked([]);
      if (sel && sids.includes(sel.session_id)) setSel(null);
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const open = async (sid) => { try { setSel((await aapi.get(`/admin/chats/${sid}`)).data); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div data-testid="admin-chats">
      <h1 className="h-display mb-6 text-3xl">{a("AI Chat Sessions", "جلسات المحادثة الذكية")} <span className="text-base text-ivory/40">({data?.length ?? "…"})</span></h1>
      {isSA && !!data?.length && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" className="chip" data-testid="chats-select-all" onClick={() => setPicked(picked.length === data.length ? [] : data.map((c) => c.session_id))}>{picked.length === data.length ? a("Clear selection", "إلغاء التحديد") : a("Select all", "تحديد الكل")}</button>
          <button type="button" className="chip !text-[#E4472B]" disabled={!picked.length} data-testid="chats-bulk-delete" onClick={() => remove(picked)}>{a(`Delete selected (${picked.length})`, `حذف المحدد (${picked.length})`)}</button>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        <ul className="max-h-[75vh] space-y-2 overflow-y-auto" data-testid="chats-list">
          {(data || []).map((c) => (
            <li key={c.session_id} className="flex items-start gap-2">
              {isSA && <input type="checkbox" className="mt-4 accent-[#C9A96E]" aria-label={a("Select session", "تحديد الجلسة")} checked={picked.includes(c.session_id)} onChange={() => toggle(c.session_id)} data-testid={`chat-select-${c.session_id}`} />}
              <button type="button" onClick={() => open(c.session_id)} data-testid={`chat-session-${c.session_id}`} className={`w-full rounded-sm border p-3 text-start text-sm ${sel?.session_id === c.session_id ? "border-gold bg-gold/10" : "border-ivory/10 hover:border-gold/50"}`}>
                <p className="truncate text-ivory" dir="auto">{c.first_message || "—"}</p>
                <p className="mt-1 text-[11px] text-ivory/45">{fmtDT(c.updated_at, lang)} · {c.messages} msgs · {c.lang}{c.score ? ` · ${c.score}` : ""}{c.lead_id ? ` · ${a("lead saved", "تم حفظ العميل")}` : ""}</p>
              </button>
            </li>
          ))}
          {data && !data.length && <p className="text-sm text-ivory/50">{a("No chat sessions yet.", "لا توجد جلسات بعد.")}</p>}
        </ul>
        <div className="glass min-h-[300px] rounded-sm p-5" data-testid="chat-transcript">
          {!sel ? <p className="text-sm text-ivory/50">{a("Select a session to view the transcript.", "اختر جلسة لعرض المحادثة.")}</p> : (
            <>
              {isSA && <button type="button" className="chip mb-3 !text-[#E4472B]" data-testid="chat-delete" onClick={() => remove([sel.session_id])}>{a("Delete this session", "حذف هذه الجلسة")}</button>}
              <p className="mb-3 text-xs text-ivory/50">{sel.summary} {sel.lead_id && <a className="text-gold" href={`/admin/leads?id=${sel.lead_id}`}>→ {a("Open lead", "فتح العميل")}</a>}</p>
              <div className="space-y-3">{sel.messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-end" : ""}><div className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-sm px-3 py-2 text-start text-sm ${m.role === "user" ? "bg-gold/85 text-ink" : "bg-ivory/[0.06]"}`} dir="auto">{m.content}{m.cards?.length ? <p className="mt-1 text-[10px] text-ivory/45">cards: {m.cards.length}</p> : null}</div><p className="text-[10px] text-ivory/35">{fmtDT(m.at, lang)}</p></div>
              ))}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const a = useA();
  const { data } = useAdminApi("/admin/integrations");
  return (
    <div data-testid="admin-integrations">
      <h1 className="h-display mb-2 text-3xl">{a("Smart-home Integrations", "تكاملات المنزل الذكي")}</h1>
      <p className="mb-6 max-w-2xl text-sm text-ivory/55">{a("Providers implement the SmartHomeProvider interface (backend/integrations.py, see INTEGRATIONS.md). None is active until credentials and an adapter are added.", "يطبق المزوّدون واجهة SmartHomeProvider. لا يوجد مزود نشط قبل إضافة بيانات الاعتماد والمحوّل.")}</p>
      <div className="grid gap-4 md:grid-cols-3">
        {(data || []).map((p) => (
          <div key={p.key} className="glass rounded-sm p-5" data-testid={`integration-${p.key}`}>
            <p className="font-display text-xl">{p.name}</p>
            <p data-testid={`integration-status-${p.key}`} className={`mt-2 inline-block rounded-sm px-2 py-0.5 text-xs ${p.status === "not_configured" ? "bg-ivory/10 text-ivory/60" : "bg-gold/15 text-gold"}`}>{p.status === "not_configured" ? a("Not configured", "غير مُعد") : a("Credentials set — adapter pending", "بيانات الاعتماد موجودة — المحوّل قيد التطوير")}</p>
            <p className="mt-3 text-xs text-ivory/55">{p.capabilities.join(" · ")}</p>
            <p className="mt-2 text-[11px] text-ivory/40" dir="ltr">env: {p.env_vars.join(", ")}</p>
            <a href={p.docs_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs text-gold">{a("API docs", "التوثيق")} →</a>
          </div>
        ))}
      </div>
    </div>
  );
}

const EVENTS = [["new_lead", "New lead", "عميل جديد"], ["hot_lead", "Hot lead", "عميل ساخن"], ["new_appointment", "New appointment", "موعد جديد"], ["appointment_reminder", "Appointment in 24h reminder", "تذكير بموعد خلال ٢٤ ساعة"]];

export function NotifyPrefs({ open, setOpen }) {
  const a = useA();
  const [p, setP] = useState(null);
  useEffect(() => { if (open) aapi.get("/auth/me/notify-prefs").then((r) => setP(r.data)); }, [open]);
  const save = async () => { try { await aapi.put("/auth/me/notify-prefs", p); toast.success(a("Preferences saved", "تم حفظ التفضيلات")); setOpen(false); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="border-gold/20 bg-ink-2 text-ivory" data-testid="notify-prefs-dialog">
        <DialogTitle className="h-display text-2xl">{a("Notification settings", "إعدادات الإشعارات")}</DialogTitle>
        {p && (
          <table className="w-full text-sm"><thead><tr className="text-[11px] uppercase text-ivory/50"><th className="p-2 text-start">{a("Event", "الحدث")}</th><th className="p-2">{a("In-app", "داخل النظام")}</th><th className="p-2">{a("Email", "البريد")}</th></tr></thead>
            <tbody>{EVENTS.map(([k, en, ar]) => (
              <tr key={k} className="border-t border-ivory/5"><td className="p-2">{a(en, ar)}</td>
                {["in_app", "email"].map((ch) => (<td key={ch} className="p-2 text-center"><input type="checkbox" className="h-4 w-4 accent-[#C9A96E]" checked={!!p[ch]?.[k]} onChange={(e) => setP({ ...p, [ch]: { ...p[ch], [k]: e.target.checked } })} data-testid={`pref-${ch}-${k}`} /></td>))}
              </tr>))}</tbody></table>
        )}
        <p className="text-xs text-ivory/45">{a("Email delivery also requires “Also email assigned agent / managers” to be enabled in Settings.", "يتطلب البريد أيضاً تفعيل خيار مراسلة الوكيل/المديرين في الإعدادات.")}</p>
        <button type="button" className="btn-gold w-fit" onClick={save} data-testid="notify-prefs-save">{a("Save", "حفظ")}</button>
      </DialogContent>
    </Dialog>
  );
}


export function DocsPage() {
  const a = useA();
  const { data } = useAdminApi("/admin/docs");
  const get = async (name) => {
    try { await download(`/admin/docs/${name}`, {}, name); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div data-testid="admin-docs-page">
      <h1 className="h-display mb-2 text-3xl">{a("Documentation", "التوثيق")}</h1>
      <p className="mb-6 text-sm text-ivory/55">{a("Handover documents (also in /app/docs). API reference:", "وثائق التسليم (موجودة أيضاً في /app/docs). مرجع الواجهة البرمجية:")} <a className="text-gold underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`} target="_blank" rel="noopener noreferrer">/api/docs</a> · <a className="text-gold underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/openapi.json`} target="_blank" rel="noopener noreferrer">/api/openapi.json</a></p>
      <ul className="grid gap-3 md:grid-cols-2">
        {(data || []).map((d) => (
          <li key={d.name} className="glass flex items-center justify-between gap-4 rounded-sm p-4">
            <div><p className="text-ivory">{d.title}</p><p className="text-xs text-ivory/45">{d.name} · {(d.size / 1024).toFixed(1)} KB</p></div>
            <button type="button" className="chip" data-testid={`doc-download-${d.name}`} onClick={() => get(d.name)}>{a("Download", "تحميل")}</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
